import React, { useState } from 'react';
import classNames from 'classnames';

import styles from './MediaThumbnail.module.scss';

import Icon from '../../widget/images/Icon';

import { Media } from '../../../dataTypes/mediaManagment/types';
import { Video } from '../../../dataTypes/mediaManagment/video_types';
import { AudioSlideshow } from '../../../dataTypes/mediaManagment/audioSlideshow_types';

import { WWW_URLS } from '../../../backendServices/urls';
import { getImageById, downloadImageBlob } from '../../../backendServices/mediaManagment/api_image';

import { convertDateToString } from '../../../dataTypes/timeManagment/utils';

// ✅ Composant MediaThumbnail séparé
interface MediaThumbnailProps {
  media?: Media;
  thumbnailWidth: number;
  thumbnailHeight: number;
  onMediaClick?: (mediaId: number) => void; // ✨ Maintenant optionnel
  showCaption?: boolean; // ✨ Nouveau prop pour afficher/masquer le texte
  toDownload?: boolean; // ✨ Contrôle le téléchargement de l'image (true par défaut)
}

export const MediaThumbnail: React.FC<MediaThumbnailProps> = ({
  media,
  thumbnailWidth,
  thumbnailHeight,
  onMediaClick,
  showCaption = true, // Par défaut, on affiche le texte
  toDownload = true // Par défaut, on télécharge l'image
}) => {
  const [thumbnailState, setThumbnailState] = useState<{
    src: string | null;
    loading: boolean;
    progress: { loaded: number; total: number } | null;
  }>({
    src: null,
    loading: false,
    progress: null
  });

  // Déterminer l'icône selon le type de média
  const getIconForMediaType = (mediaType: string): string => {
    switch (mediaType) {
      case 'Video':
        return 'filmStrip';
      case 'Image':
        return 'palette';
      case 'Sound':
        return 'musicalNotes';
      default:
        return 'file';
    }
  };

  // Charger l'image via blob si l'URL échoue
  const loadImageBlob = async () => {
    if (!media || thumbnailState.loading) return;

    // Pour un AudioSlideshow, la vignette est celle de l'Image liée (media.id est
    // l'id du slideshow lui-même, pas celui de l'image).
    const imageIdToLoad = media.mediaType === 'AudioSlideshow'
      ? (media as AudioSlideshow).image_id
      : media.id;

    if (!imageIdToLoad) return;

    setThumbnailState(prev => ({ ...prev, loading: true, progress: { loaded: 0, total: 0 } }));

    try {
      const getImageByIdResult = await getImageById(imageIdToLoad);
      if (getImageByIdResult.code === 1) {
        const completeImageStruct = await downloadImageBlob(
          getImageByIdResult.payload,
          (progress) => {
            setThumbnailState(prev => ({ ...prev, progress }));
          }
        );
        if (completeImageStruct.objectURL) {
          setThumbnailState({
            src: completeImageStruct.objectURL,
            loading: false,
            progress: null
          });
        } else {
          console.error("objectURL is undefined");
          setThumbnailState(prev => ({ ...prev, loading: false, progress: null }));
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement de l'image:", error);
      setThumbnailState(prev => ({ ...prev, loading: false, progress: null }));
    }
  };

  // Gérer l'erreur de chargement de la thumbnail
  const handleThumbnailError = () => {
    // ✨ Ne tenter le téléchargement que si toDownload est true
    if (toDownload) {
      loadImageBlob();
    }
  };

  // Calculer la chaîne de durée
  let durationStr = '';
  if (media && media.mediaType === 'Video' && (media as Video).duration) {
    durationStr = (media as Video).duration;
  } else if (
    media &&
    media.mediaType === 'AudioSlideshow' &&
    (media as AudioSlideshow).imageDisplayDuration
  ) {
    durationStr = (media as AudioSlideshow).imageDisplayDuration;
  }

  let ch = media ? convertDateToString(media.creationDate, 'dd/MM/yyyy') : '';
  ch += ch !== '' && durationStr !== '' ? ' / ' : '';
  ch += durationStr;

  // URL de la thumbnail par défaut
  const thumbnailUrl = media ? `${WWW_URLS.MediaServer}${media.publicRef}/images/thumbnail.webp` : '';

  // ✨ Calcul de la hauteur totale selon si on affiche le texte ou non
  const totalHeight = showCaption ? thumbnailHeight + 100 : thumbnailHeight;

  return (
    <div
      className={classNames(styles.thumbnail)}
      onClick={media && onMediaClick ? () => onMediaClick(media.id) : undefined}
      style={{
        width: thumbnailWidth,
        height: totalHeight,
        cursor: media && onMediaClick ? 'pointer' : 'default'
      }}
    >
      {media && (
        <>
          <div
            className={classNames(styles.line_1)}
            style={{
              width: thumbnailWidth,
              height: thumbnailHeight,
              backgroundColor: 'var(--color-6)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: '1px solid var(--color-M)',
              borderRadius: '8px',
              position: 'relative'
            }}
          >
            {/* Afficher l'icône pendant le chargement ou si pas de source */}
            {(!thumbnailState.src && !thumbnailState.loading) && (
              <Icon
                name={getIconForMediaType(media.mediaType)}
                width={48}
                height={48}
                color="var(--color-0)"
              />
            )}

            {/* ✨ Afficher la progression uniquement si toDownload est true */}
            {toDownload && thumbnailState.loading && thumbnailState.progress && (
              <div style={{ textAlign: 'center' }}>
                <Icon
                  name={getIconForMediaType(media.mediaType)}
                  width={48}
                  height={48}
                  color="var(--color-0)"
                />
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-M)' }}>
                  {thumbnailState.progress.total > 0
                    ? `${Math.round((thumbnailState.progress.loaded / thumbnailState.progress.total) * 100)}%`
                    : 'Chargement...'}
                </div>
              </div>
            )}

            {/* ✨ Si toDownload est false, juste afficher l'icône sans progression */}
            {!toDownload && thumbnailState.loading && (
              <Icon
                name={getIconForMediaType(media.mediaType)}
                width={48}
                height={48}
                color="var(--color-0)"
              />
            )}

            {/* Image thumbnail - essai avec URL puis fallback sur blob */}
            {toDownload && !thumbnailState.src && !thumbnailState.loading && (
              <img
                src={thumbnailUrl}
                alt={media.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
                onError={handleThumbnailError}
              />
            )}

            {/* Image chargée via blob */}
            {toDownload && thumbnailState.src && (
              <img
                src={thumbnailState.src}
                alt={media.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '8px'
                }}
              />
            )}
          </div>

          {/* ✨ Affichage conditionnel du texte */}
          {showCaption && (
            <div className={classNames(styles.line_2, styles.text)} style={{ marginTop: 8, textAlign: 'center' }}>
              {media.name} ({ch})
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MediaThumbnail;